import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ButtonDirective } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { retry } from 'rxjs';
import {
	CdkDragDrop,
	CdkDrag,
	CdkDropList,
	CdkDragHandle,
	CdkDragPlaceholder,
	moveItemInArray,
} from '@angular/cdk/drag-drop';
import { SectionCardComponent } from '../section-card/section-card';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { AudioTrackEditorComponent } from '../audio-track-editor/audio-track-editor';
import { DimensionsEditorComponent } from '../dimensions-editor/dimensions-editor';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor';
import { VariantEditorComponent } from '../variant-editor/variant-editor';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { ProjectsService } from '../../../core/services/projects.service';
import { EditorDataService } from '../../../features/editor/services/editor-data.service';
import { EditorSlugsService } from '../../../features/editor/services/editor-slugs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { AudioRef, ChapterData, SceneRef, FileCategory, ProjectFile, SceneData } from '../../models';
import { deriveNamesMapKey, deriveSlug, generateEntityId, makeUniqueSlug } from '../../utils/slug';
import { cloneDeep } from '../../utils/clone';
import { shouldStampVideoNp } from '../../utils/media-readiness';
import { setRefVariant, type VariantLike, type VariantRefLike } from '../../utils/variant-fallback';

@Component({
	selector: 'app-chapter-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		FormsModule,
		InputText,
		ButtonDirective,
		Checkbox,
		CdkDrag,
		CdkDropList,
		CdkDragHandle,
		CdkDragPlaceholder,
		SectionCardComponent,
		MediaPickerComponent,
		AudioTrackEditorComponent,
		DimensionsEditorComponent,
		AttributesEditorComponent,
		VariantEditorComponent,
	],
	templateUrl: './chapter-form.html',
	styleUrl: './chapter-form.scss',
})
export class ChapterFormComponent {
	data = input.required<ChapterData>();
	projectSlug = input('');
	namesMapKeys = input<string[]>([]);
	argKeys = input<string[]>([]);
	dataChange = output<ChapterData>();
	slugRenamed = output<{ oldSlug: string; newSlug: string }>();
	sceneSelect = output<{ chapterSlug: string; sceneSlug: string }>();
	imageSelect = output<{ chapterSlug: string; imageSlug: string }>();
	namesMapPopulate = output<{ key: string; names: string[] }>();
	copyScene = output<{ chapterSlug: string; sceneSlug: string }>();
	copyImage = output<{ chapterSlug: string; imageSlug: string }>();

	protected sceneSlugs = computed(() => (this.data()?.scenes ?? []).map(s => s.slug));

	protected onSlugChange(newSlug: string): void {
		const oldSlug = this.data()?.slug;
		if (!newSlug?.trim() || newSlug === oldSlug) return;
		this.slugRenamed.emit({ oldSlug, newSlug: newSlug.trim() });
	}

	protected patch(patcher: (draft: ChapterData) => void): void {
		const clone = cloneDeep(this.data() ?? ({} as ChapterData));
		patcher(clone);
		this.dataChange.emit(clone);
	}

	protected onAudioTrackChange(tracks: AudioRef[]): void {
		const oldLen = (this.data().audioTrack ?? []).length;
		const added = tracks.length > oldLen;
		this.patch(d => { d.audioTrack = tracks; });
		if (!added) return;
		// Auto-populate namesMap for variant groups
		for (const track of tracks) {
			if (!track.src?.includes('[VARIANT]')) continue;
			const media = this.filesService.findByBaseUrl(track.src);
			if (!media?.variants?.length) continue;
			const key = deriveNamesMapKey(media.name ?? 'variant');
			if (key) this.namesMapPopulate.emit({ key, names: media.variants.map(v => v.name) });
		}
	}

	// ── Scenes ────────────────────────────────────────────────────────────────
	protected splitSlugs(scene: SceneRef): string[] {
		const n = scene.split;
		if (!n || n <= 1) return [];
		const ext = scene.externalChapter;
		const base = ext ? ext.substring(ext.indexOf('/') + 1) : scene.slug;
		return Array.from({ length: n }, (_, i) => `${base}__${i}`);
	}

	private dataService = inject(EditorDataService);
	private filesService = inject(EditorFilesService);
	private jobsService = inject(EditorJobsService);
	private destroyRef = inject(DestroyRef);
	private projectsService = inject(ProjectsService);
	private stateService = inject(EditorStateService);
	private slugsService = inject(EditorSlugsService);
	private messageService = inject(MessageService);

	protected addScene(): void {
		const name = prompt('Scene name:');
		if (!name?.trim()) return;
		const trimmed = name.trim();
		const chapter = this.data();
		const existing = this.slugsService.takenSceneSlugs(chapter.scenes ?? [], chapter.slug);
		const s = makeUniqueSlug(deriveSlug(trimmed) || 'scene', existing);
		const id = generateEntityId();
		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			d.scenes.push({ id, slug: s });
		});
		// Scene doc carries name; ref does not
		const scenePath = `/projects/${this.projectSlug()}/chapters/${chapter.slug}/scenes/${s}/scene.json`;
		const defaultScene = { id, name: trimmed, slug: s, framesCount: 0, playbackType: 'VIDEO_NP', background: { source: { srcType: 'ABSOLUTE' } }, content: [] };
		this.dataService.saveFiles(new Map([[scenePath, defaultScene]]), 'add-scene', { create: true }).subscribe();
	}

	// ── Add scene from video file ───────────────────────────────────────────
	protected sceneVideoPickerVisible = signal(false);
	protected readonly sceneVideoPickerCategories: FileCategory[] = ['video'];

	protected openSceneVideoPicker(): void {
		this.sceneVideoPickerVisible.set(true);
	}

	protected onSceneVideosSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const chapter = this.data();
		const files = new Map<string, unknown>();
		const newScenes: { id: string; slug: string; variant?: { type: 'NAME'; key: string; namesMapKey?: string } }[] = [];
		const existingSlugs = this.slugsService.takenSceneSlugs(chapter.scenes ?? [], chapter.slug);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'scene';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'scene', existingSlugs);
			existingSlugs.add(slug);

			const fps = media?.fps || 25;
			const framesCount = media?.durationSeconds ? Math.round(media.durationSeconds * fps) : 0;
			const id = generateEntityId();

			if (baseUrl.includes('[VARIANT]')) {
				const namesMapKey = deriveNamesMapKey(media?.name ?? slug);
				newScenes.push({ id, slug, variant: { type: 'NAME', key: '', namesMapKey } });
			} else {
				newScenes.push({ id, slug });
			}
			// Scene doc carries name; ref does not
			const scenePath = `/projects/${this.projectSlug()}/chapters/${chapter.slug}/scenes/${slug}/scene.json`;
			const sceneEntry = {
				id, name, slug, framesCount,
				// Only when a generation job will follow to clear it again (DEV-530).
				...(shouldStampVideoNp(media) ? { playbackType: 'VIDEO_NP' } : {}),
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'VIDEO' } },
				content: [],
			};
			files.set(scenePath, sceneEntry);
			// Seed sceneMap so the post-gen clearPlaybackTypeForMedia scan finds
			// these scenes — same pattern as editor.ts onTimelineFileDrop.
			this.stateService.setScene(scenePath, sceneEntry as unknown as SceneData);
		}
		if (!newScenes.length) return;

		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			for (const s of newScenes) (d.scenes as unknown[]).push(s);
		});
		// Populate namesMap for variant groups — emitted separately, parent merges atomically
		for (const baseUrl of urls.filter(u => u.includes('[VARIANT]'))) {
			const media = mediaByUrl.get(baseUrl);
			if (!media?.variants?.length) continue;
			const key = deriveNamesMapKey(media.name ?? 'variant');
			if (key) this.namesMapPopulate.emit({ key, names: media.variants.map(v => v.name) });
		}
		this.dataService.saveFiles(files, 'bulk-add-scenes', { create: true }).subscribe();
		this.autoGenerateForMedia(mediaByUrl);
	}

	// ── Add scene from image file ───────────────────────────────────────────
	protected sceneImagePickerVisible = signal(false);
	protected readonly sceneImagePickerCategories: FileCategory[] = ['images'];

	protected openSceneImagePicker(): void {
		this.sceneImagePickerVisible.set(true);
	}

	protected onSceneImagesSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const chapter = this.data();
		const files = new Map<string, unknown>();
		const newScenes: { id: string; slug: string }[] = [];
		const existingSlugs = this.slugsService.takenSceneSlugs(chapter.scenes ?? [], chapter.slug);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'scene';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'scene', existingSlugs);
			existingSlugs.add(slug);

			const fps = media?.fps || 25;
			// Images have no probed duration — default to a 3s end-screen-style
			// hold, same as the drag&drop path in editor.ts onTimelineFileDrop.
			const framesCount = Math.round(3 * fps);
			const id = generateEntityId();

			newScenes.push({ id, slug });
			// Scene doc carries name; ref does not
			const scenePath = `/projects/${this.projectSlug()}/chapters/${chapter.slug}/scenes/${slug}/scene.json`;
			const sceneEntry = {
				id, name, slug, framesCount,
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'IMAGE' } },
				content: [],
			};
			files.set(scenePath, sceneEntry);
			// Seed sceneMap so the post-gen clearPlaybackTypeForMedia scan finds
			// these scenes — same pattern as editor.ts onTimelineFileDrop.
			this.stateService.setScene(scenePath, sceneEntry as unknown as SceneData);
		}
		if (!newScenes.length) return;

		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			for (const s of newScenes) (d.scenes as unknown[]).push(s);
		});
		this.dataService.saveFiles(files, 'bulk-add-scenes', { create: true }).subscribe();
		this.autoGenerateForMedia(mediaByUrl);
	}

	// ── Add images from files ────────────────────────────────────────────────
	protected imagePickerVisible = signal(false);
	protected readonly imagePickerCategories: FileCategory[] = ['images'];

	protected openImagePicker(): void {
		this.imagePickerVisible.set(true);
	}

	protected onImagesSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const chapter = this.data();
		const files = new Map<string, unknown>();
		const newImages: { id: string; slug: string }[] = [];
		const existingSlugs = this.slugsService.takenImageSlugs(chapter.images ?? [], chapter.slug);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'image';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'image', existingSlugs);
			existingSlugs.add(slug);

			const id = generateEntityId();
			newImages.push({ id, slug });
			// Image doc carries name; ref does not
			const imagePath = `/projects/${this.projectSlug()}/chapters/${chapter.slug}/images/${slug}/image.json`;
			files.set(imagePath, {
				id, name, slug,
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'IMAGE' } },
				content: [],
			});
		}
		if (!newImages.length) return;

		this.patch(d => {
			if (!d.images) d.images = [];
			for (const img of newImages) (d.images as unknown[]).push(img);
		});
		this.dataService.saveFiles(files, 'bulk-add-images', { create: true }).pipe(retry({ count: 1, delay: 500 })).subscribe({
			error: (err) => {
				// The image.json write failed even after a retry — drop the refs we
				// just added so a broken reference (no matching image doc) never
				// reaches project.json. Without this, the player 404s on the
				// missing image at startup and crashes the whole preview.
				const failedSlugs = new Set(newImages.map(img => img.slug));
				this.patch(d => {
					d.images = (d.images ?? []).filter((img: { slug: string }) => !failedSlugs.has(img.slug));
				});
				this.messageService.add({
					severity: 'error', summary: 'Failed to add image',
					detail: err?.error?.error ?? 'Could not save the image content. Please try again.',
				});
			},
		});
		this.autoGenerateForMedia(mediaByUrl);
	}

	private autoGenerateForMedia(mediaByUrl: ReadonlyMap<string, ProjectFile | undefined>): void {
		for (const media of mediaByUrl.values()) {
			const mediaId = media?.id;
			if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
				this.filesService.generateVersions(mediaId)
					.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
					.subscribe();
			}
		}
	}

	// ── Add audio tracks from files ─────────────────────────────────────────
	protected audioTrackPickerVisible = signal(false);
	protected readonly audioTrackPickerCategories: FileCategory[] = ['audios'];

	protected openAudioTrackPicker(): void {
		this.audioTrackPickerVisible.set(true);
	}

	protected onAudioTracksSelected(urls: string[]): void {
		const existing = new Set((this.data()?.audioTrack ?? []).map((a: { slug: string }) => a.slug));
		const newTracks: { id: string; name: string; slug: string; src: string; srcType: string }[] = [];
		for (const baseUrl of urls) {
			const media = this.filesService.findByBaseUrl(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'audio';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'audio', existing);
			existing.add(slug);
			newTracks.push({ id: generateEntityId(), name, slug, src: baseUrl, srcType: 'ABSOLUTE' });
		}
		this.patch(d => {
			if (!d.audioTrack) d.audioTrack = [];
			for (const t of newTracks) (d.audioTrack as unknown[]).push(t);
		});
	}

	protected onSceneClick(scene: SceneRef, event: MouseEvent): void {
		event.stopPropagation();
		const chapter = this.data();
		let chapterSlug: string;
		let sceneSlug: string;
		const ext = scene.externalChapter;
		if (ext) {
			const slash = ext.indexOf('/');
			chapterSlug = ext.substring(0, slash);
			sceneSlug = ext.substring(slash + 1);
		} else {
			chapterSlug = chapter.slug;
			sceneSlug = scene.slug;
		}
		this.sceneSelect.emit({ chapterSlug, sceneSlug });
	}

	protected onSplitSceneClick(scene: SceneRef, splitSlug: string, event: MouseEvent): void {
		event.stopPropagation();
		const chapter = this.data();
		const ext = scene.externalChapter;
		const chapterSlug = ext ? ext.substring(0, ext.indexOf('/')) : chapter.slug;
		this.sceneSelect.emit({ chapterSlug, sceneSlug: splitSlug });
	}

	protected onImageClick(image: { slug: string }, event: MouseEvent): void {
		event.stopPropagation();
		const chapter = this.data();
		this.imageSelect.emit({ chapterSlug: chapter.slug, imageSlug: image.slug });
	}

	protected dropScene(event: CdkDragDrop<unknown[]>): void {
		this.patch(d => moveItemInArray(d.scenes, event.previousIndex, event.currentIndex));
	}

	protected dropImage(event: CdkDragDrop<unknown[]>): void {
		this.patch(d => { if (d.images) moveItemInArray(d.images, event.previousIndex, event.currentIndex); });
	}

	protected requestCopyScene(index: number, event: MouseEvent): void {
		event.stopPropagation();
		const slug = this.data().scenes?.[index]?.slug;
		if (slug) this.copyScene.emit({ chapterSlug: this.data().slug, sceneSlug: slug });
	}

	protected requestCopyImage(index: number, event: MouseEvent): void {
		event.stopPropagation();
		const slug = (this.data().images as { slug: string }[])?.[index]?.slug;
		if (slug) this.copyImage.emit({ chapterSlug: this.data().slug, imageSlug: slug });
	}

	protected removeScene(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this scene?')) return;
		const slug = this.data().scenes?.[index]?.slug;
		const chapterSlug = this.data().slug;
		this.patch(d => d.scenes.splice(index, 1));
		if (slug) this.projectsService.deleteScene(this.projectSlug(), slug, chapterSlug)
			.subscribe(() => this.slugsService.forgetScene(chapterSlug, slug));
	}

	protected removeImage(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this image?')) return;
		const slug = (this.data().images as { slug: string }[])?.[index]?.slug;
		const chapterSlug = this.data().slug;
		this.patch(d => { d.images?.splice(index, 1); });
		if (slug) this.projectsService.deleteImage(this.projectSlug(), slug, chapterSlug)
			.subscribe(() => this.slugsService.forgetImage(chapterSlug, slug));
	}

	// ── Expand/attributes on scene+image refs ─────────────────────────────────
	protected expandedScene = signal<number | null>(null);
	protected expandedImage = signal<number | null>(null);

	protected toggleSceneExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedScene.update(v => v === index ? null : index);
	}

	protected toggleImageExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedImage.update(v => v === index ? null : index);
	}

	/**
	 * The chapter's own attribute map (DEV-833).
	 *
	 * Reads `groups` (plural) and `group` (singular) alike — the player accepts
	 * both and imported projects carry the singular — but {@link
	 * setChapterAttributes} only ever writes `groups`, as every other producer
	 * in the platform does.
	 */
	protected chapterAttributes = computed<Record<string, unknown> | null>(() => {
		const chapter = this.data() as unknown as Record<string, unknown> | undefined;
		return (chapter?.['attributes'] as Record<string, unknown> | undefined) ?? null;
	});

	/**
	 * Gate the whole chapter. Same shape as a scene ref's attributes — the
	 * player's `getAvailableChapter()` reads `group`/`groups` + `attributes` off
	 * the chapter and skips it when they do not match, so one map here replaces
	 * the same condition repeated on every scene.
	 */
	protected setChapterAttributes(attrs: Record<string, unknown>): void {
		this.patch(d => {
			const chapter = d as unknown as Record<string, unknown>;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				chapter['attributes'] = attrs;
				chapter['groups'] = keys;
			} else {
				delete chapter['attributes'];
				delete chapter['groups'];
			}
			// A stale singular `group` from an import would out-vote `groups` in
			// the player's first branch, so it goes when we take ownership.
			delete chapter['group'];
		});
	}

	protected setSceneAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patch(d => {
			const scene = d.scenes?.[index] as unknown as Record<string, unknown> | undefined;
			if (!scene) return;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				scene['attributes'] = attrs;
				scene['groups'] = keys;
			} else {
				delete scene['attributes'];
				delete scene['groups'];
			}
		});
	}

	protected setSceneVariant(index: number, variant: VariantLike | null): void {
		this.patch(d => {
			const scenes = (d.scenes ?? []) as unknown as VariantRefLike[];
			const scene = scenes[index];
			if (scene) setRefVariant(scenes, scene, variant);
		});
	}

	protected setSceneAlt(index: number, alt: boolean): void {
		this.patch(d => {
			const scene = d.scenes?.[index] as unknown as VariantRefLike | undefined;
			if (!scene) return;
			if (alt) scene.alt = true;
			else delete scene.alt;
		});
	}

	protected getSceneSlugs(): string[] {
		return (this.data()?.scenes ?? []).map(s => s.slug);
	}

	protected getAudioSlugs(): string[] {
		return (this.data()?.audioTrack ?? []).map((a: { slug: string }) => a.slug);
	}

	protected setImageAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patch(d => {
			const image = d.images?.[index] as unknown as Record<string, unknown> | undefined;
			if (!image) return;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				image['attributes'] = attrs;
				image['groups'] = keys;
			} else {
				delete image['attributes'];
				delete image['groups'];
			}
		});
	}
}
